import type {
  ExtendedResource,
  SupabaseClient,
  Tables,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import {
  createInPredefinedListFilter,
  createListContainsFilter,
  DataTableQueryBuilder,
  ensureColumnFilter,
} from "~/lib/utils/datatable-query-builder"
import {
  QUERYABLE_RESOURCE_STATUSES,
  type QueryableResourceStatus,
} from "./resources.constants"
import type { AddResourceData, UpdateResourceData } from "./resources.schema"

export interface Resource extends Tables<"resources"> {}

export const createResourcesService = (supabase: SupabaseClient) => {
  return new ResourcesService(supabase)
}

class ResourcesService {
  log = logger.child({
    module: "resources-service",
  })

  constructor(private readonly supabase: SupabaseClient) {}

  async getOneExtended({ unitId }: { unitId: string }) {
    const { data, error } = await this.supabase
      .from("extended_resources")
      .select("*")
      .eq("unit_id", unitId)
      .maybeSingle()

    if (error) {
      this.log.error("Error fetching resource profile", { error })
      throw new Error(error.message)
    }

    if (!data) {
      this.log.warn("Resource profile not found", { unitId })
      throw new Error("Resource profile not found")
    }

    return data as ExtendedResource
  }

  async getOneExtendedById({ id }: { id: string }) {
    const { data, error } = await this.supabase
      .from("extended_resources")
      .select("*")
      .eq("id", id)
      .maybeSingle()

    if (error) {
      this.log.error("Error fetching resource by id", { error })
      throw new Error(error.message)
    }

    if (!data) {
      this.log.warn("Resource not found", { id })
      throw new Error("Resource not found")
    }

    return data
  }

  async getOneExtendedBySlug({ slug }: { slug: string }) {
    const { data, error } = await this.supabase
      .from("extended_resources")
      .select("*")
      .eq("slug", slug)
      .maybeSingle()

    if (error) {
      this.log.error("Error fetching resource by slug", { error })
      throw new Error(error.message)
    }

    return data as ExtendedResource
  }

  async getManyExtended(
    filters?: DataTableBaseSearch & {
      business_entity_slugs?: string[]
      client_slugs?: string[]
    },
  ) {
    const builder = new DataTableQueryBuilder(this.supabase, {
      table: "extended_resources",
      searchField: "fts_resource",
      defaultSort: { field: "created_at", ascending: false },
      filters: {
        status: createInPredefinedListFilter(
          "status",
          QUERYABLE_RESOURCE_STATUSES,
        ),
        business_entity_slugs: createListContainsFilter(
          "business_entity_slugs",
        ),
        client_slugs: createListContainsFilter("client_slugs"),
      },
    })

    const filtersWithColumnFilters: DataTableBaseSearch = filters
      ? { ...filters }
      : { page: 1, page_size: 20 }

    let columnFilters = filters?.columnFilters || []

    if (filters?.business_entity_slugs?.length) {
      columnFilters = ensureColumnFilter(columnFilters, {
        id: "business_entity_slugs",
        value: filters.business_entity_slugs,
      })
    }

    if (filters?.client_slugs?.length) {
      columnFilters = ensureColumnFilter(columnFilters, {
        id: "client_slugs",
        value: filters.client_slugs,
      })
    }

    filtersWithColumnFilters.columnFilters = columnFilters

    const { data, count } = await builder.execute<ExtendedResource>(
      filtersWithColumnFilters,
    )

    return {
      resources: data,
      count,
    }
  }

  async setStatus({
    resourceId,
    status,
  }: {
    resourceId: string
    status: QueryableResourceStatus
  }) {
    const { data, error } = await this.supabase
      .from("resources")
      .update({ status })
      .eq("id", resourceId)
      .in("status", QUERYABLE_RESOURCE_STATUSES) // We don't want to allow updating the status of an archived resource
      .select("id, status")
      .single()

    if (error) {
      this.log.error("Error setting resource status", { error })
      throw new Error(error.message)
    }

    return data
  }

  async create(userId: string, data: AddResourceData) {
    // Map frontend schema to database function parameters
    const skills = data.skills?.map((skill) => skill.skill) || null
    const languages = data.languages?.length > 0 ? data.languages : null
    const address = data.address ?? null
    const bankingDetails = data.banking ?? null

    // Call the database function
    const { data: result, error } = await this.supabase.rpc("create_resource", {
      p_user_id: userId,
      p_first_name: data.firstName,
      p_last_name: data.lastName,
      p_email: data.email,
      p_phone: data.phone || undefined,
      p_status: "pending",
      p_vat_number: data.vatNumber || undefined,
      p_skills: skills,
      p_experience: undefined,
      p_iana_timezone_name: data.ianaTimezoneName,
      p_address: address ?? undefined,
      p_banking_details: bankingDetails ?? undefined,
      p_languages: languages ?? undefined,
      p_business_entity_unit_id: data.businessEntityUnitId ?? undefined,
    })

    if (error) {
      this.log.error("Error creating resource", { error })
      throw new Error("Failed to create resource", { cause: error })
    }

    this.log.info("Resource created successfully", {
      resourceId: result.resource_id,
      unitId: result.unit_id,
      userId: userId,
      email: data.email,
      businessEntityUnitId: data.businessEntityUnitId,
    })

    return result
  }

  async update(data: UpdateResourceData) {
    // Map frontend schema to database function parameters
    const skills = data.skills?.map((skill) => skill.skill) || null
    const languages = data.languages?.length > 0 ? data.languages : null
    const address = Object.hasOwn(data, "address")
      ? (data.address ?? null)
      : undefined
    const bankingDetails = Object.hasOwn(data, "banking")
      ? (data.banking ?? null)
      : undefined

    const { data: result, error } = await this.supabase.rpc("update_resource", {
      p_resource_id: data.id,
      p_first_name: data.firstName ?? undefined,
      p_last_name: data.lastName ?? undefined,
      p_email: data.email ?? undefined,
      p_phone: data.phone ?? undefined,
      p_status: data.status ?? undefined,
      p_vat_number: data.vatNumber ?? undefined,
      p_skills: skills ?? undefined,
      p_iana_timezone_name: data.ianaTimezoneName ?? undefined,
      p_address: address,
      p_banking_details: bankingDetails,
      p_languages: languages ?? undefined,
    })

    if (error) {
      this.log.error("Error updating resource", { error })
      throw new Error(error.message)
    }

    const response = Array.isArray(result) ? result[0] : result

    if (!response) {
      throw new Error("No data returned from update_resource")
    }

    this.log.info("Resource updated successfully", {
      resourceId: response.resource_id,
      unitId: response.unit_id,
      email: data.email,
    })

    return response
  }

  async archive({ resourceId }: { resourceId: string }) {
    const { error } = await this.supabase.rpc("soft_delete_resources", {
      p_id: resourceId,
    })

    if (error) {
      this.log.error("Error archiving resource", { error })
      throw new Error(error.message)
    }

    this.log.info("Resource archived successfully", { resourceId })
  }

  async getProjects({ resourceUnitId }: { resourceUnitId: string }) {
    const { data, error } = await this.supabase
      .from("project_resources")
      .select("*, project:projects(*), rates:project_resource_rates(*)")
      .eq("resource_unit_id", resourceUnitId)
      .eq("status", "active")

    if (error) {
      this.log.error("Error fetching resource projects", { error })
      throw new Error(error.message)
    }

    return data
  }
}
